import React, { useState, useRef, useEffect } from 'react';
import { View, TouchableOpacity, Image, Modal, TextInput, ActivityIndicator } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faTimes } from '@fortawesome/free-solid-svg-icons';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import CustomButton from '../../../components/CustomButton';

type Steps = 'waiting' | 'capturing' | 'previewImage';

interface Props {
    onConfirm: (attachment: { imageUrl: string; description?: string; }) => void;
}

export default function TakePicture({ onConfirm }: Props) {
    const { styles } = useStyles(stylesheet);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [step, setStep] = useState<Steps>('waiting');
    const [image, setImage] = useState<string | null>(null);
    const [description, setDescription] = useState<string>('');

    const handleOpenCamera = () => {
        setStep('capturing');
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';

        if (!file) {
            setStep('waiting');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setImage(reader.result as string);
            setStep('previewImage');
        };
        reader.onerror = () => {
            setStep('waiting');
        };
        reader.readAsDataURL(file);
    };

    useEffect(() => {
        const input = fileInputRef.current;
        if (!input) {
            return;
        }

        const handleCancel = () => {
            setStep((current) => (current === 'capturing' ? 'waiting' : current));
        };

        input.addEventListener('cancel', handleCancel);
        return () => input.removeEventListener('cancel', handleCancel);
    }, []);

    const resetAndClose = () => {
        setImage(null);
        setDescription('');
        setStep('waiting');
    };

    return (
        <>
            <input
                type='file'
                accept='image/*'
                // capture='environment' makes camera open directly,
                // because is the behavior in the android-native-pwa-builder
                capture='environment'
                style={{ display: 'none' }}
                onChange={handleFileChange}
                ref={(input) => (fileInputRef.current = input)}
            />

            {step === 'waiting' &&
                <CustomButton
                    onPress={handleOpenCamera}
                    style={{ padding: 12 }}
                    icon={faCamera}
                    text={translate('camera')}
                />
            }

            {(step === 'capturing' || step === 'previewImage') &&
                <Modal
                    animationType="slide"
                    transparent={false}
                    onRequestClose={resetAndClose}
                >
                    <View style={styles.webcamContainer}>
                        <TouchableOpacity
                            style={styles.closeCameraBtn}
                            onPress={resetAndClose}
                        >
                            <FontAwesomeIcon icon={faTimes} color={'#fff'} fontSize={24} />
                        </TouchableOpacity>

                        {step === 'capturing' &&
                            <View style={styles.loadingView}>
                                <ActivityIndicator size="large" color="#fff" style={styles.loading} />
                            </View>
                        }

                        {step === 'previewImage' && image &&
                            <>
                                <Image
                                    style={{ flex: 1 }}
                                    resizeMode='contain'
                                    source={{ uri: image }}
                                />

                                <View style={styles.descriptionContainer}>
                                    <TextInput
                                        style={styles.descriptionInput}
                                        placeholder={translate('attachmentDescription')}
                                        placeholderTextColor="#ccc"
                                        value={description}
                                        onChangeText={setDescription}
                                    />
                                </View>

                                <TouchableOpacity onPress={resetAndClose}>
                                    <View style={styles.cancelButton}>
                                        <MyAppText style={styles.buttonsText}>{translate('cancel')}</MyAppText>
                                    </View>
                                </TouchableOpacity>
                                <TouchableOpacity onPress={() => {
                                    if (image) {
                                        onConfirm({ imageUrl: image, description: description.trim() || undefined });
                                    }
                                    resetAndClose();
                                }}>
                                    <View style={styles.confirmButton}>
                                        <MyAppText style={styles.buttonsText}>{translate('confirm')}</MyAppText>
                                    </View>
                                </TouchableOpacity>
                            </>
                        }
                    </View>
                </Modal>
            }
        </>
    );
}

const stylesheet = createStyleSheet(() => ({
    webcamContainer: {
        backgroundColor: '#000',
        width: '100%',
        height: '100%'
    },
    loading: {
        marginHorizontal: 'auto',
        transform: [{ scaleX: 2 }, { scaleY: 2 }],
    },
    loadingView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    closeCameraBtn: {
        position: 'absolute',
        top: 40,
        left: 20,
        width: 50,
        height: 50,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        borderRadius: 25,
        zIndex: 10,
    },
    descriptionContainer: {
        position: 'absolute',
        bottom: 220,
        left: 20,
        right: 20,
    },
    descriptionInput: {
        backgroundColor: 'rgba(0, 0, 0, 0.50)',
        color: '#fff',
        borderRadius: 8,
        paddingHorizontal: 14,
        paddingVertical: 10,
        fontSize: 14,
    },
    cancelButton: {
        position: 'absolute',
        bottom: 80,
        left: 130,
        transform: [{ translateX: -65 }],
        justifyContent: 'center',
        alignItems: 'center',
        height: 130,
        width: 130,
        borderRadius: 20,
        backgroundColor: 'rgba(0, 0, 0, 0.50)',
    },
    confirmButton: {
        position: 'absolute',
        bottom: 80,
        right: 0,
        transform: [{ translateX: -65 }],
        justifyContent: 'center',
        alignItems: 'center',
        height: 130,
        width: 130,
        borderRadius: 20,
        backgroundColor: 'rgba(0, 0, 0, 0.50)',
    },
    buttonsText: {
        color: '#fff',
        fontSize: 24
    }
}));
